import { Text, Box, Flex, Loader } from '@mantine/core';
import { useMemo } from 'react';
import { StepCard } from './StepCard';
import { useFileUploads } from 'src/data-capture/hooks/file-uploads/useFileUploads';
import { useSelectableUploadItems } from '../hooks/useSelectableUploadItems';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { SuitabilitySelectableList } from '../edit/components/SuitabilitySelectableList';
import { useUploadFiles } from '../hooks/useUploadFile';
import { UploadPillButton } from '../edit/components/UploadPillButton';
import { StepProps } from '../types';
import { SelectedCountPill } from '../edit/components/SelectedCountPill';
import { getClientId } from '../edit/utils/getClientId';
import { useFilteredItems } from '../hooks/useFilteredItems';
import { useItemSelectionMapping } from '../hooks/useItemSelectionMapping';

export function FeeDocumentsStep({ data, setData, filter }: StepProps) {
  const clientId = getClientId(data);
  const { files, isLoading } = useFileUploads({ clientId });
  const items = useSelectableUploadItems(files);

  const selectedIds = useMemo(() => data.feeDocuments.map(d => d.id), [data.feeDocuments]);
  const selectedCount = data.feeDocuments.length;

  const { isUploading, handleFileUpload } = useUploadFiles({
    clientId,
    onUploaded: uploadedFiles => {
      setData(prev => ({
        ...prev,
        feeDocuments: [...prev.feeDocuments, ...uploadedFiles],
      }));
    },
  });

  const filteredItems = useFilteredItems(items, filter);
  const mapIdsToSelectedFiles = useItemSelectionMapping(items);

  const title = data.isFamily
    ? `Include fee documentation for ${data.family?.name} family`
    : `Include fee documentation for ${data.client?.firstName} ${data.client?.lastName}`;

  return (
    <Flex direction="column" h="100%" mih={0}>
      <StepCard
        title={title}
        subTitle="Select any documents that contain fee information to be referenced in the report. This includes provider fee schedules, platform charges, fund costs, and adviser fee agreements."
      >
        <Flex align="center" justify="space-between" w="100%" mb="lg" p="xs">
          <SelectedCountPill selectedCount={selectedCount} />
          <Text fw={FONT_WEIGHTS.medium} size="xs" c="gray.4" flex={1} miw={0} mx={16}>
            Only include documents that contain fee-related information. The fees extracted from
            these documents will be used to populate fee disclosures in the suitability report.
          </Text>
          <UploadPillButton
            loading={isUploading}
            onFilesSelected={handleFileUpload}
            label="Upload"
          />
        </Flex>
        <Box flex={1} mih={0} style={{ overflow: 'auto' }}>
          {isLoading ? (
            <Flex justify="center" align="center" h="100%">
              <Loader data-testid="loader" />
            </Flex>
          ) : (
            <SuitabilitySelectableList
              items={filteredItems.map(f => ({
                id: f.id,
                title: f.title,
                description: f.description,
                file: f.file,
              }))}
              selectMultiple
              selectedItemIds={selectedIds}
              setSelectedItemIds={ids =>
                setData(prev => ({
                  ...prev,
                  feeDocuments: mapIdsToSelectedFiles(ids),
                }))
              }
              highlightedElement={data.highlightedElement}
              emptyView={<Text c="gray.6">No fee documents found</Text>}
            />
          )}
        </Box>
      </StepCard>
    </Flex>
  );
}
